import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { CaretDown } from "@phosphor-icons/react";
import Navbar from "../components/Navbar";
import PairsList from "../components/PairsList";
import Chart from "../components/Chart";
import OrderBook from "../components/OrderBook";
import TradePanel from "../components/TradePanel";
import BottomTabs from "../components/BottomTabs";
import api, { fmtPct, fmtUsd } from "../lib/api";
import { Drawer, DrawerContent, DrawerTrigger, DrawerTitle, DrawerHeader } from "../components/ui/drawer";

const MOBILE_TABS = [
  { key: "chart", label: "Chart" },
  { key: "book", label: "Order Book" },
  { key: "trade", label: "Trade" },
  { key: "positions", label: "Positions" },
];

export default function TradePage() {
  const { symbol: paramSym } = useParams();
  const navigate = useNavigate();
  const [symbol, setSymbol] = useState(paramSym || localStorage.getItem("lastSymbol") || "BTCUSDT");
  const [markPrice, setMarkPrice] = useState(0);
  const [changePct, setChangePct] = useState(0);
  const [marketClosed, setMarketClosed] = useState(false);
  const [tradedAt, setTradedAt] = useState(0);
  const [mobileTab, setMobileTab] = useState("chart");
  const [pairsOpen, setPairsOpen] = useState(false);

  useEffect(() => {
    if (paramSym && paramSym !== symbol) setSymbol(paramSym);
  }, [paramSym]);

  // Remember the last viewed pair so returning to Trade doesn't reset to BTC
  useEffect(() => {
    if (symbol) localStorage.setItem("lastSymbol", symbol);
  }, [symbol]);

  useEffect(() => {
    if (!symbol) return;
    // Reset price immediately on pair switch so stale values aren't applied to the new chart
    setMarkPrice(0);
    let alive = true;
    const load = () => api.get(`/market/symbols`).then((r) => {
      if (!alive) return;
      const m = r.data.find((s) => s.symbol === symbol);
      if (m) { setMarkPrice(m.price); setChangePct(m.change_pct); setMarketClosed(m.category === "stock" && !m.market_open); }
    }).catch(() => {});
    load();
    const i = setInterval(load, 1500);
    return () => { alive = false; clearInterval(i); };
  }, [symbol]);

  const onSelect = (s) => {
    setSymbol(s);
    setPairsOpen(false);
    navigate(`/trade/${s}`);
  };

  // Click a position → load that pair's chart (and jump to Chart tab on mobile)
  const openChartFor = (s) => {
    setSymbol(s);
    navigate(`/trade/${s}`);
    setMobileTab("chart");
  };

  return (
    <div className="flex flex-col h-screen overflow-hidden bg-[#0A0E17] text-white">
      <Navbar />

      {/* DESKTOP LAYOUT (lg+) */}
      <div className="hidden lg:grid flex-1 grid-cols-[16rem_1fr_20rem] grid-rows-[1fr_18rem] gap-px bg-[#1F2937] overflow-hidden min-h-0">
        <div className="row-span-2 min-h-0">
          <PairsList active={symbol} onSelect={onSelect} />
        </div>
        <div className="min-h-0">
          <Chart symbol={symbol} marketClosed={marketClosed} markPrice={markPrice} />
        </div>
        <div className="row-span-2 flex flex-col min-h-0">
          <div className="flex-1 min-h-0">
            <OrderBook symbol={symbol} markPrice={markPrice} />
          </div>
          <TradePanel symbol={symbol} markPrice={markPrice} onTraded={() => setTradedAt(Date.now())} />
        </div>
        <div className="min-h-0">
          <BottomTabs refresh={tradedAt} onSelectSymbol={openChartFor} />
        </div>
      </div>

      {/* MOBILE LAYOUT (<lg) */}
      <div className="lg:hidden flex flex-col flex-1 min-h-0">
        {/* Symbol header */}
        <Drawer open={pairsOpen} onOpenChange={setPairsOpen}>
          <DrawerTrigger asChild>
            <button
              className="flex items-center justify-between px-4 py-2 bg-[#111827] border-b border-[#1F2937] active:bg-[#1F2937]"
              data-testid="mobile-symbol-selector">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-base">{symbol.replace("USDT", "/USDT")}</span>
                {marketClosed && (
                  <span className="px-1.5 py-0.5 rounded-full text-[9px] font-medium bg-[#F6465D]/15 text-[#F6465D]" data-testid="mobile-market-closed-badge">
                    Закрыт
                  </span>
                )}
                <CaretDown size={14} weight="bold" className="text-[#9CA3AF]" />
              </div>
              <div className="flex items-center gap-3">
                <span className="mono text-base font-semibold">${fmtUsd(markPrice, markPrice < 1 ? 4 : 2)}</span>
                <span className={`mono text-xs ${changePct >= 0 ? "text-[#0ECB81]" : "text-[#F6465D]"}`}>
                  {fmtPct(changePct)}
                </span>
              </div>
            </button>
          </DrawerTrigger>
          <DrawerContent className="bg-[#0A0E17] border-[#1F2937] h-[85vh]">
            <DrawerHeader className="border-b border-[#1F2937]">
              <DrawerTitle className="text-white text-base">Select market</DrawerTitle>
            </DrawerHeader>
            <div className="flex-1 overflow-hidden">
              <PairsList active={symbol} onSelect={onSelect} />
            </div>
          </DrawerContent>
        </Drawer>

        {/* Tabs */}
        <div className="flex gap-1 p-1.5 border-b border-[#1F2937] bg-[#111827] shrink-0 overflow-x-auto">
          {MOBILE_TABS.map((t) => (
            <button key={t.key} onClick={() => setMobileTab(t.key)}
              data-testid={`mobile-tab-${t.key}`}
              className={`flex-1 min-w-[80px] py-2 rounded-md text-xs font-medium tracking-wide whitespace-nowrap transition-colors ${mobileTab === t.key ? "bg-white/[0.06] text-[#E3B341] ring-1 ring-inset ring-white/[0.08]" : "text-[#6B7280] hover:text-white hover:bg-white/[0.06]"}`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div className="flex-1 min-h-0 overflow-hidden">
          {mobileTab === "chart" && <Chart symbol={symbol} marketClosed={marketClosed} markPrice={markPrice} />}
          {mobileTab === "book" && <OrderBook symbol={symbol} markPrice={markPrice} />}
          {mobileTab === "trade" && (
            <div className="h-full overflow-y-auto">
              <TradePanel symbol={symbol} markPrice={markPrice} onTraded={() => { setTradedAt(Date.now()); setMobileTab("positions"); }} />
            </div>
          )}
          {mobileTab === "positions" && <BottomTabs refresh={tradedAt} onSelectSymbol={openChartFor} />}
        </div>
      </div>
    </div>
  );
}
